import { BadgeIconDef, ICON_FILL_TOKEN } from './types.js'

const F = `${ICON_FILL_TOKEN}`

/**
 * Stroke-only line icons on the 64 grid, round caps and joins. The renderer sets fill="none",
 * the stroke width and the paint; `{fill}` marks small solid parts filled with the metal.
 * Base set ported from docs/nope-badges/badges.config.js; the rest drawn in the same style.
 */
export const BADGE_ICONS: Record<string, BadgeIconDef> = {
    // --- base set (badges.config.js, generated) ---
    briefcase: {
        label: { et: 'Portfell', en: 'Briefcase' },
        center: [32, 33.35],
        svg: '<rect x="9" y="22" width="46" height="31" rx="6"/><path d="M24 22v-6a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v6M9 36h46"/>',
    },
    mic: {
        label: { et: 'Mikrofon', en: 'Microphone' },
        center: [32, 32.5],
        svg: '<rect x="23" y="6" width="18" height="30" rx="9"/><path d="M14 29c0 11 8 17 18 17s18-6 18-17M32 46v10M22 57h20"/>',
    },
    sparkle: {
        label: { et: 'Säde', en: 'Sparkle' },
        center: [33.85, 30.2],
        svg: '<g transform="translate(1.5 3.5)"><path d="M29 7c2 14 8 20 22 22-14 2-20 8-22 22-2-14-8-20-22-22 14-2 20-8 22-22z"/><path d="M50 5c.8 4 2.6 5.6 6 6.2-3.4.6-5.2 2.2-6 6.2-.8-4-2.6-5.6-6-6.2 3.4-.6 5.2-2.2 6-6.2z" {fill}/></g>',
    },
    trophy: {
        label: { et: 'Karikas', en: 'Trophy' },
        center: [32, 31.25],
        svg: '<path d="M20 9h24v15c0 9-5 15-12 15s-12-6-12-15z"/><path d="M20 14h-8c0 9 3 13 9 14M44 14h8c0 9-3 13-9 14M32 39v8M21 56h22M25 56v-9h14v9"/>',
    },
    heart: {
        label: { et: 'Süda', en: 'Heart' },
        center: [32, 30.05],
        svg: '<path d="M32 54C15 43 7 34 7 23c0-8 6-13 13-13 5 0 10 3 12 8 2-5 7-8 12-8 7 0 13 5 13 13 0 11-8 20-25 31z"/>',
    },
    gift: {
        label: { et: 'Kingitus', en: 'Gift' },
        center: [32, 32.6],
        svg: '<rect x="11" y="28" width="42" height="27" rx="4"/><rect x="8" y="19" width="48" height="10" rx="4"/><path d="M32 19v36M32 19c-4-8-14-10-15-4-1 4 8 5 15 4zM32 19c4-8 14-10 15-4 1 4-8 5-15 4z"/>',
    },
    cat: {
        label: { et: 'Kass', en: 'Cat' },
        center: [32, 30.45],
        svg: '<path d="M13 31L15 9l12 10c3-1 7-1 10 0l12-10 2 22c1 12-7 21-19 21S12 43 13 31z"/><circle cx="24.5" cy="32" r="3.4" {fill} stroke="none"/><circle cx="39.5" cy="32" r="3.4" {fill} stroke="none"/><path d="M29.5 40h5l-2.5 3z"/>',
    },
    dog: {
        label: { et: 'Koer', en: 'Dog' },
        center: [32, 32],
        // Sitting puppy seen from the front with floppy ears, as the old Luna-time image (Nele, 19.10.2026).
        svg: '<g stroke-width="4.5"><path d="M24 8h16c4 0 7 3 7 7v6c0 8-6 13-15 13s-15-5-15-13v-6c0-4 3-7 7-7z"/><path d="M19 10c-7 1-10 9-8 18 1 3 5 3 6 0M45 10c7 1 10 9 8 18-1 3-5 3-6 0"/><circle cx="27" cy="19" r="2.2" {fill} stroke="none"/><circle cx="37" cy="19" r="2.2" {fill} stroke="none"/><path d="M29 26h6l-3 3z" {fill}/><path d="M20 34c-6 4-8 12-6 22h36c2-10 0-18-6-22"/><path d="M24 56v-8M40 56v-8"/></g>',
    },
    globe: {
        label: { et: 'Gloobus', en: 'Globe' },
        center: [32, 32],
        svg: '<circle cx="32" cy="32" r="24"/><ellipse cx="32" cy="32" rx="10" ry="24"/><path d="M8 32h48M12 19c13 4 27 4 40 0M12 45c13-4 27-4 40 0"/>',
    },
    book: {
        label: { et: 'Raamat', en: 'Book' },
        center: [32, 32.6],
        svg: '<path d="M32 17c-7-5-15-6-24-5v38c9-1 17 0 24 5 7-5 15-6 24-5V12c-9-1-17 0-24 5zM32 17v38"/>',
    },
    dice: {
        label: { et: 'Täring', en: 'Dice' },
        center: [32, 32],
        svg: '<rect x="9" y="9" width="46" height="46" rx="11"/><circle cx="21" cy="21" r="4.4" {fill} stroke="none"/><circle cx="43" cy="21" r="4.4" {fill} stroke="none"/><circle cx="32" cy="32" r="4.4" {fill} stroke="none"/><circle cx="21" cy="43" r="4.4" {fill} stroke="none"/><circle cx="43" cy="43" r="4.4" {fill} stroke="none"/>',
    },
    alarm: {
        label: { et: 'Äratuskell', en: 'Alarm clock' },
        center: [32.1, 32.95],
        svg: '<circle cx="32" cy="35" r="19"/><path d="M32 25v10l7 5M10 19c1-6 6-9 12-8M54 19c-1-6-6-9-12-8M19 52l-4 5M45 52l4 5"/>',
    },
    gear: {
        label: { et: 'Hammasratas', en: 'Gear' },
        center: [32, 32],
        svg: '<path d="M50.17,26.44L58.67,27.78L58.67,36.22L50.17,37.56L48.78,40.92L53.84,47.87L47.87,53.84L40.92,48.78L37.56,50.17L36.22,58.67L27.78,58.67L26.44,50.17L23.08,48.78L16.13,53.84L10.16,47.87L15.22,40.92L13.83,37.56L5.33,36.22L5.33,27.78L13.83,26.44L15.22,23.08L10.16,16.13L16.13,10.16L23.08,15.22L26.44,13.83L27.78,5.33L36.22,5.33L37.56,13.83L40.92,15.22L47.87,10.16L53.84,16.13L48.78,23.08Z"/><circle cx="32" cy="32" r="8"/>',
    },
    cap: {
        label: { et: 'Akadeemiline müts', en: 'Graduation cap' },
        center: [32.95, 30.6],
        svg: '<path d="M3 25L32 13l29 12-29 12z"/><path d="M16 31v12c8 6 24 6 32 0V31M57 27v15"/>',
    },
    glasses: {
        label: { et: 'Prillid', en: 'Glasses' },
        center: [32, 34.85],
        svg: '<circle cx="19" cy="36" r="11"/><circle cx="45" cy="36" r="11"/><path d="M30 34c1-3 3-3 4 0M8 34L5 23M56 34l3-11"/>',
    },
    people: {
        label: { et: 'Inimesed', en: 'People' },
        center: [32.55, 32.45],
        svg: '<circle cx="24" cy="21" r="8.5"/><circle cx="45" cy="25" r="6.5"/><path d="M8 53c0-10 6-17 16-17s16 7 16 17M42 38c1-.6 2-.8 3.4-.8 7 0 11 5 11 14"/>',
    },
    buoy: {
        label: { et: 'Päästerõngas', en: 'Lifebuoy' },
        center: [32, 32],
        svg: '<circle cx="32" cy="32" r="24"/><circle cx="32" cy="32" r="10"/><path d="M15 15l10 10M49 15L39 25M15 49l10-10M49 49L39 39"/>',
    },
    braces: {
        label: { et: 'Loogelised sulud', en: 'Braces' },
        center: [32, 32],
        svg: '<path d="M21 9c-6 0-8 3-8 8v8c0 4-2 7-6 7 4 0 6 3 6 7v8c0 5 2 8 8 8M43 9c6 0 8 3 8 8v8c0 4 2 7 6 7-4 0-6 3-6 7v8c0 5-2 8-8 8M37 20L27 44"/>',
    },
    bracesStar: {
        label: { et: 'Sulud karikaga', en: 'Braces with trophy' },
        center: [32, 33],
        // Hackathon MVP: small {*} over a trophy, as the old image; distinct from Hackathon's {/}.
        svg: '<g transform="translate(32 17) scale(0.5) translate(-32 -32)"><path d="M21 9c-6 0-8 3-8 8v8c0 4-2 7-6 7 4 0 6 3 6 7v8c0 5 2 8 8 8M43 9c6 0 8 3 8 8v8c0 4 2 7 6 7-4 0-6 3-6 7v8c0 5-2 8-8 8M32 22v20M23.3 27l17.4 10M23.3 37l17.4-10" stroke-width="9"/></g><g stroke-width="5.5"><path d="M20 35h24v8a12 12 0 0 1-24 0z"/><path d="M32 55v4M24 59h16"/></g>',
    },
    bulb: {
        label: { et: 'Lambipirn', en: 'Light bulb' },
        center: [32, 32.6],
        svg: '<path d="M23 42c-6-4-10-10-10-17 0-11 8-18 19-18s19 7 19 18c0 7-4 13-10 17v4H23z"/><path d="M24 52h16M27 58h10"/>',
    },
    sun: {
        label: { et: 'Päike', en: 'Sun' },
        center: [32, 32],
        svg: '<circle cx="32" cy="32" r="11"/><path d="M50 32L58 32M44.73 44.73L50.38 50.38M32 50L32 58M19.27 44.73L13.62 50.38M14 32L6 32M19.27 19.27L13.62 13.62M32 14L32 6M44.73 19.27L50.38 13.62"/>',
    },
    snow: {
        label: { et: 'Lumehelves', en: 'Snowflake' },
        center: [32, 32],
        svg: '<path d="M32 7L32 57M32 47L25.87 53.59M32 47L38.13 53.59M32 17L38.13 10.41M32 17L25.87 10.41M10.35 19.5L53.65 44.5M44.99 39.5L47.63 48.11M44.99 39.5L53.76 37.48M19.01 24.5L16.37 15.89M19.01 24.5L10.24 26.52M53.65 19.5L10.35 44.5M19.01 39.5L10.24 37.48M19.01 39.5L16.37 48.11M44.99 24.5L53.76 26.52M44.99 24.5L47.63 15.89"/>',
    },
    wind: {
        label: { et: 'Tuul', en: 'Wind' },
        center: [31.65, 31.5],
        svg: '<path d="M6 24h32c6 0 9-3 9-7s-3-7-7-7-7 3-7 6M6 36h42c6 0 9 3 9 7s-3 7-7 7-7-3-7-6M6 48h22"/>',
    },
    target: {
        label: { et: 'Sihtmärk', en: 'Target' },
        center: [32, 32],
        svg: '<circle cx="32" cy="32" r="18"/><circle cx="32" cy="32" r="5" {fill} stroke="none"/><path d="M32 5v10M32 49v10M5 32h10M49 32h10"/>',
    },
    cake: {
        label: { et: 'Tort', en: 'Cake' },
        center: [32, 37.9],
        svg: '<path d="M10 55h44V38a6 6 0 0 0-6-6H16a6 6 0 0 0-6 6z"/><path d="M10 45c4 0 4 3 8 3s4-3 8-3 4 3 8 3 4-3 8-3 4 3 8 3"/><path d="M32 32v-8"/><path d="M32 11c-2.5 3-4 5-4 7a4 4 0 0 0 8 0c0-2-1.5-4-4-7z" {fill} stroke-width="2"/>',
    },
    clapper: {
        label: { et: 'Klapp', en: 'Clapperboard' },
        center: [32, 33.4],
        svg: '<rect x="8" y="27" width="48" height="28" rx="5"/><path d="M9 27l3-13h43l-3 13M24 14l-4 13M39 14l-4 13"/>',
    },
    // --- drawn for the remaining Hub badges ---
    sunrise: {
        label: { et: 'Päikesetõus', en: 'Sunrise' },
        center: [32, 36],
        svg: '<path d="M32 12v8M14 20l5 5M50 20l-5 5M8 40h8M48 40h8"/><path d="M19 40a13 13 0 0 1 26 0"/><path d="M8 50h48"/>',
    },
    lunch: {
        label: { et: 'Lõuna', en: 'Lunch' },
        center: [32, 32],
        svg: '<path d="M20 8v48M13 8v12a7 7 0 0 0 14 0V8"/><path d="M44 56V8c7 5 9 18 4 28h-4"/>',
    },
    tshirt: {
        label: { et: 'T-särk', en: 'T-shirt' },
        center: [32, 33],
        svg: '<path d="M22 10l10 4 10-4 14 8-6 10-6-3v31H20V25l-6 3-6-10z"/>',
    },
    waves: {
        label: { et: 'Lained', en: 'Waves' },
        center: [32, 32],
        svg: '<path d="M6 22c6 0 6-6 13-6s7 6 13 6 6-6 13-6 7 6 13 6M6 36c6 0 6-6 13-6s7 6 13 6 6-6 13-6 7 6 13 6M6 50c6 0 6-6 13-6s7 6 13 6 6-6 13-6 7 6 13 6"/>',
    },
    shoe: {
        label: { et: 'Jooksujalats', en: 'Running shoe' },
        center: [32, 36],
        svg: '<path d="M8 44h40a8 8 0 0 0 8-8l-14-6-6-10H24l-4 10H8z"/><path d="M26 30l6 6M32 24l6 6M8 50h48"/>',
    },
    whistle: {
        label: { et: 'Vile', en: 'Whistle' },
        center: [32, 36],
        // Pea whistle as in the prototype (Nele, 19.10.2026): thinner stroke so the mouthpiece stays a shape, not a stick.
        svg: '<g stroke-width="5"><path d="M38 27H24a13 13 0 1 0 13 13v-3"/><path d="M38 27l20-7v12l-20 4"/><circle cx="24" cy="40" r="4.5"/><path d="M38 27v13"/></g>',
    },
    key: {
        label: { et: 'Võti', en: 'Key' },
        center: [32, 32],
        svg: '<circle cx="20" cy="44" r="12"/><path d="M29 35L54 10M46 18l6 6M40 24l6 6"/>',
    },
    masks: {
        label: { et: 'Teatrimaskid', en: 'Theatre masks' },
        center: [32, 33],
        svg: '<path d="M8 12c0 24 6 32 16 32s16-8 16-32c-10 4-22 4-32 0z"/><path d="M16 22h5M27 22h5M18 32c3 3 9 3 12 0"/><path d="M42 20c5 2 9 2 14 0 0 24-5 32-14 32"/><path d="M48 30h4M46 42c2-3 6-3 8 0"/>',
    },
    ribbon: {
        label: { et: 'Aumärk', en: 'Award ribbon' },
        center: [32, 33],
        svg: '<circle cx="32" cy="24" r="14"/><path d="M22 35l-6 21 16-8 16 8-6-21"/>',
    },
    moon: {
        label: { et: 'Kuu', en: 'Moon' },
        center: [33, 32],
        svg: '<path d="M38 8a22 22 0 1 0 18 34A18 18 0 0 1 38 8z"/>',
    },
    pancakes: {
        label: { et: 'Pannkoogid', en: 'Pancakes' },
        center: [32, 34],
        svg: '<ellipse cx="32" cy="24" rx="20" ry="6"/><path d="M12 24v8c0 3 9 6 20 6s20-3 20-6v-8M12 32v8c0 3 9 6 20 6s20-3 20-6v-8M12 40v8c0 3 9 6 20 6s20-3 20-6v-8"/><path d="M26 18c2-6 6-8 6-8s4 2 6 8"/>',
    },
    paw: {
        label: { et: 'Käpajälg', en: 'Paw' },
        center: [32, 34],
        svg: `<circle cx="20" cy="22" r="5" ${F} stroke="none"/><circle cx="44" cy="22" r="5" ${F} stroke="none"/><circle cx="10" cy="34" r="4.5" ${F} stroke="none"/><circle cx="54" cy="34" r="4.5" ${F} stroke="none"/><path d="M32 30c-10 0-16 10-16 16s4 8 8 8c4 0 6-2 8-2s4 2 8 2 8-2 8-8-6-16-16-16z"/>`,
    },
    handHeart: {
        label: { et: 'Süda peos', en: 'Heart in hand' },
        center: [31, 33],
        // Open hand (Lucide hand-heart's hand) with a heart centred above it (Nele, 15.10.2026).
        svg: '<g transform="translate(2 8) scale(2.5)" stroke-width="2.8"><path d="M11 14h2a2 2 0 0 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 16"/><path d="m2 15 6 6"/><path d="m7 20 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a1 1 0 0 0-2.75-2.91"/></g><path d="M31 25s-13-7-13-15a6.5 6.5 0 0 1 13-3 6.5 6.5 0 0 1 13 3c0 8-13 15-13 15z"/>',
    },
    presentation: {
        label: { et: 'Esitlus', en: 'Presentation' },
        center: [32, 32],
        svg: '<rect x="8" y="10" width="48" height="32" rx="4"/><path d="M32 42v12M22 54h20M28 19l10 7-10 7z"/>',
    },
    star: {
        label: { et: 'Täht', en: 'Star' },
        center: [32, 33],
        svg: '<path d="M32 8l7 15 16 2-12 11 3 16-14-8-14 8 3-16L9 25l16-2z"/>',
    },
    wrench: {
        label: { et: 'Mutrivõti', en: 'Wrench' },
        center: [32, 32],
        svg: '<path d="M54 18l-8 8-8-8 8-8a12 12 0 0 0-16 14L9 45l10 10 21-21a12 12 0 0 0 14-16z"/>',
    },
    merge: {
        label: { et: 'Haruühendus', en: 'Merge' },
        center: [32, 32],
        svg: '<circle cx="18" cy="14" r="6"/><circle cx="18" cy="50" r="6"/><circle cx="46" cy="32" r="6"/><path d="M18 20v24M18 24c0 8 8 8 22 8"/>',
    },
    flag: {
        label: { et: 'Lipp', en: 'Flag' },
        center: [31, 32],
        svg: '<path d="M14 56V10M14 12h34l-8 10 8 10H14"/>',
    },
    handshake: {
        label: { et: 'Käepigistus', en: 'Handshake' },
        center: [32, 30],
        // Lucide "handshake" (ISC) scaled ×2.5; stroke 2 (=5 in the 64 grid) because the ~7 stroke turns a handshake into a blob.
        svg: '<g transform="translate(2 2) scale(2.5)" stroke-width="2"><path d="m11 17 2 2a1 1 0 1 0 3-3"/><path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4"/><path d="m21 3 1 11h-2"/><path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3"/><path d="M3 4h8"/></g>',
    },
    party: {
        label: { et: 'Pidu', en: 'Party' },
        center: [32, 34],
        svg: '<path d="M8 56l10-28 18 18z"/><path d="M28 28c4-8 14-10 22-6M36 36c8-4 16 0 20 8M40 12l2 6M52 20l-5 3M22 14l4 4"/>',
    },
    userPlus: {
        label: { et: 'Uus inimene', en: 'Add person' },
        center: [32, 33],
        svg: '<circle cx="26" cy="20" r="10"/><path d="M8 54c0-12 8-18 18-18s18 6 18 18M48 20v16M40 28h16"/>',
    },
    eye: {
        label: { et: 'Silm', en: 'Eye' },
        center: [32, 32],
        svg: '<path d="M4 32c8-12 18-18 28-18s20 6 28 18c-8 12-18 18-28 18S12 44 4 32z"/><circle cx="32" cy="32" r="8"/>',
    },
    megaphone: {
        label: { et: 'Ruupor', en: 'Megaphone' },
        center: [32, 32],
        svg: '<path d="M8 25v14h9l23 11V14L17 25z"/><path d="M47 23a11 11 0 0 1 0 18M53 15a20 20 0 0 1 0 34"/>',
    },
    diploma: {
        label: { et: 'Diplom', en: 'Diploma' },
        center: [32, 33],
        svg: '<rect x="8" y="22" width="48" height="18" rx="9"/><path d="M27 22v18M37 22v18M32 40l-5 14M32 40l5 14"/>',
    },
    certificate: {
        label: { et: 'Tunnistus', en: 'Certificate' },
        center: [32, 33],
        svg: '<rect x="8" y="12" width="48" height="34" rx="4"/><path d="M16 22h32M16 30h20"/><circle cx="46" cy="38" r="5"/><path d="M43 42l-2 12 5-3 5 3-2-12"/>',
    },
    laptop: {
        label: { et: 'Sülearvuti', en: 'Laptop' },
        center: [32, 31],
        svg: '<rect x="12" y="12" width="40" height="28" rx="3"/><path d="M4 48h56l-4-8H8z"/>',
    },
    tent: {
        label: { et: 'Telk', en: 'Tent' },
        center: [32, 35],
        svg: '<path d="M32 12L6 54h20l6-14 6 14h20z"/><path d="M24 12l8 8 8-8"/>',
    },
    coffee: {
        label: { et: 'Kohv', en: 'Coffee' },
        center: [31, 33],
        svg: '<path d="M10 22h34v14a14 14 0 0 1-14 14H24a14 14 0 0 1-14-14z"/><path d="M44 26h6a6 6 0 0 1 0 12h-6M20 8c0 4-4 4-4 8M28 8c0 4-4 4-4 8M36 8c0 4-4 4-4 8"/>',
    },
    banana: {
        label: { et: 'Banaan', en: 'Banana' },
        center: [32, 33],
        svg: '<g stroke-width="5.5"><path d="M13 20c-3 14 7 28 23 30 8 1 15-2 20-7-7 2-15 1-21-3C26 36 19 30 16 20z"/><path d="M15 19l-4-6M56 43l5 3"/></g>',
    },
    camera: {
        label: { et: 'Kaamera', en: 'Camera' },
        center: [32, 33],
        svg: '<path d="M8 22h10l4-6h20l4 6h10v30H8z"/><circle cx="32" cy="36" r="9"/>',
    },
    rocket: {
        label: { et: 'Rakett', en: 'Rocket' },
        center: [32, 32],
        svg: '<path d="M32 6c10 6 14 18 12 34H20C18 24 22 12 32 6z"/><path d="M20 40l-8 8 4 8h32l4-8-8-8M32 40v16"/><circle cx="32" cy="24" r="5"/>',
    },
    puzzle: {
        label: { et: 'Pusletükk', en: 'Puzzle piece' },
        center: [32, 32],
        svg: '<path d="M22 10h12v6a5 5 0 0 0 10 0v-6h10v12h-6a5 5 0 0 0 0 10h6v12H42v-6a5 5 0 0 0-10 0v6H22V42h-6a5 5 0 0 1 0-10h6z"/>',
    },
    leaf: {
        label: { et: 'Leht', en: 'Leaf' },
        center: [33, 32],
        svg: '<path d="M12 52C12 30 26 14 54 12c-2 28-18 42-42 40z"/><path d="M12 52L38 26"/>',
    },
    hammer: {
        label: { et: 'Haamer', en: 'Hammer' },
        center: [32, 32],
        svg: '<path d="M36 10l18 18-8 8-18-18z"/><path d="M33 31L11 53"/>',
    },
    poster: {
        label: { et: 'Plakat', en: 'Poster' },
        center: [32, 33],
        svg: `<rect x="12" y="10" width="40" height="46" rx="4"/><circle cx="32" cy="10" r="4" ${F} stroke="none"/><path d="M22 26h20M22 36h20M22 46h12"/>`,
    },
    board: {
        label: { et: 'Tahvel', en: 'Board' },
        center: [32, 33],
        svg: '<rect x="8" y="8" width="48" height="32" rx="4"/><path d="M18 20h20M18 28h12"/><path d="M32 40v4M22 58l10-14 10 14"/>',
    },
    plane: {
        label: { et: 'Paberlennuk', en: 'Paper plane' },
        center: [33, 32],
        svg: '<path d="M8 28L56 10 40 56 30 36z"/><path d="M30 36L56 10"/>',
    },
    palette: {
        label: { et: 'Palett', en: 'Palette' },
        center: [31, 31],
        svg: `<path d="M32 8C18 8 8 18 8 32s10 24 24 24c6 0 7-5 5-8-2-4 0-8 5-8h5c6 0 9-5 9-9C56 18 46 8 32 8z"/><circle cx="20" cy="30" r="4" ${F} stroke="none"/><circle cx="30" cy="19" r="4" ${F} stroke="none"/><circle cx="43" cy="23" r="4" ${F} stroke="none"/>`,
    },
    splash: {
        label: { et: 'Vette!', en: 'Splash' },
        center: [32, 31],
        svg: '<path d="M6 48c6-6 12-6 18 0s12 6 18 0 12-6 18 0"/><path d="M32 8c-6 8-9 13-9 17a9 9 0 0 0 18 0c0-4-3-9-9-17z"/><path d="M12 32l3-8M52 32l-3-8"/>',
    },
}

export const BADGE_ICON_KEYS = Object.keys(BADGE_ICONS)

/**
 * Icon markup on the 64 grid with its optical centre moved to (32, 32), ready for
 * `BadgeArtSpec.iconMarkup`. Null for an unknown key.
 */
export const customIconMarkup = (key: string): string | null => {
    const def = BADGE_ICONS[key]
    if (!def) return null
    const [cx, cy] = def.center
    const dx = Number((32 - cx).toFixed(2))
    const dy = Number((32 - cy).toFixed(2))
    if (dx === 0 && dy === 0) return def.svg
    return `<g transform="translate(${dx} ${dy})">${def.svg}</g>`
}
